import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { go } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap } from 'rxjs/operators';

import * as actions from '../../actions';

export const COPY_OF_LIST_PREVIOUS_WORD = $localize`:ProductList|Copy of list previous word@@copyOfListPreviousWord:My`;

/**
 * ProductList Clone Effects
 */
@Injectable()
export class CloneEffects {
  private accountId$ = this.store.pipe(
    select(selectAccountId),
    filter(isNonNullish),
  );
  private siteId$ = this.store.pipe(select(selectSiteId), filter(isNonNullish));

  public cloneProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.cloneProductList),
      concatLatestFrom(() => [this.accountId$, this.siteId$]),
      switchMap(([action, accountId, siteId]) => {
        const name = `${COPY_OF_LIST_PREVIOUS_WORD} ${action.list.name}`;

        if (!action.list.id) {
          return of(actions.cloneProductListFailed({ error: 'Invalid list' }));
        }

        return this.productListApi
          .clone({
            id: action.list.id,
            name,
            accountId,
            siteId,
          })
          .pipe(
            map((idOrList) => {
              const list =
                typeof idOrList === 'string'
                  ? {
                      ...action.list,
                      id: idOrList,
                      name,
                    }
                  : idOrList;
              return actions.cloneProductListSuccess({
                list,
              });
            }),
            catchError((error: Error) =>
              of(actions.cloneProductListFailed({ error: error.message })),
            ),
          );
      }),
    ),
  );

  public redirectOnCloneSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.cloneProductListSuccess),
      concatMap((action) => [
        actions.loadProductList({ id: action.list.id }),
        go({ commands: ['browse', 'product-lists', action.list.id] }),
      ]),
    ),
  );

  public showSnackBarClonedProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.cloneProductListSuccess),
      map((action) =>
        showSnackBarMessage({
          message: $localize`:@@ClonedProductListSnackMessage:${action.list.name} cloned.`,
          messageType: MessageType.SUCCESS,
        }),
      ),
    ),
  );

  /**
   * Dependencies
   * @param actions$ required by ngRx
   * @param productListService Service to make the HttpRequests
   */
  constructor(
    private actions$: Actions,
    private productListApi: ProductListApiService,
    private store: Store,
  ) {}
}
